import { useMemo } from 'react';
import type { SeriesPieOptions } from 'highcharts';
import { PieChart } from '@forge/charts';
import { useTheme } from '@forge/common';
import type { StatusDistribution } from '@/api/dashboard';
import ChartCard from './ChartCard';
import { getStatusColors } from './chartColors';

interface StatusDonutChartProps {
  distribution: StatusDistribution;
}

const StatusDonutChart = ({ distribution }: StatusDonutChartProps) => {
  const { isDark } = useTheme();

  const series = useMemo<SeriesPieOptions[]>(() => {
    const colors = getStatusColors(isDark);

    const data = [
      { name: 'Done', y: distribution.done, color: colors.done },
      { name: 'In progress', y: distribution.inProgress, color: colors.in_progress },
      { name: 'Todo', y: distribution.todo, color: colors.todo },
      { name: 'Blocked', y: distribution.blocked, color: colors.blocked },
    ].filter((point) => point.y > 0);

    return [
      {
        type: 'pie',
        name: 'Milestones',
        innerSize: '68%',
        data,
      },
    ];
  }, [distribution, isDark]);

  return (
    <ChartCard
      title="Milestone status"
      subtitle={`${distribution.total} milestones tracked`}
    >
      <div className="relative">
        <PieChart
          series={series}
          chart={{ height: 300 }}
          legend={{ enabled: true, align: 'center', verticalAlign: 'bottom' }}
          tooltip={{
            pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)',
          }}
          plotOptions={{
            pie: {
              showInLegend: true,
              dataLabels: { enabled: false },
              borderWidth: 2,
            },
          }}
        />
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center pb-10">
          <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {distribution.done}
          </span>
          <span className="text-xs font-medium uppercase tracking-wide text-color-tertiary">
            Done
          </span>
        </div>
      </div>
    </ChartCard>
  );
};

export type { StatusDonutChartProps };
export default StatusDonutChart;
